import classNames from 'classnames';
import { useMemo } from 'react';
import type { SingleValue } from 'react-select';

import { Text } from '@revelio/ui';

import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type {
  ChartType,
  SelectableChartType,
} from '../PageConfigProvider/types/chart';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import { ChartIcon, NonSelectableChartIcon } from './ChartIcon';
import styles from './ChartTypeSelector.module.css';
import layoutStyles from './chart-type-selector.module.css';

interface ChartTypeOption {
  value: ChartType;
  label: string;
  icon: React.ReactElement;
  isUserSelectable: boolean;
}

const CHART_TYPE_OPTIONS: ChartTypeOption[] = [
  // User-selectable
  {
    value: 'bar',
    label: 'Bar',
    icon: <ChartIcon type="bar" />,
    isUserSelectable: true,
  },
  {
    value: 'map',
    label: 'Map',
    icon: <ChartIcon type="map" />,
    isUserSelectable: true,
  },
  {
    value: 'stacked-bar',
    label: 'Stacked Bar',
    icon: <ChartIcon type="stacked-bar" />,
    isUserSelectable: true,
  },
  {
    value: 'grouped-bar',
    label: 'Grouped bar',
    icon: <ChartIcon type="grouped-bar" />,
    isUserSelectable: true,
  },
  {
    value: 'diverging-bar',
    label: 'Diverging bar',
    icon: <ChartIcon type="diverging-bar" />,
    isUserSelectable: true,
  },
  {
    value: 'diverging-bar-combo',
    label: 'Diverging bar combo',
    icon: <ChartIcon type="diverging-bar-combo" />,
    isUserSelectable: true,
  },
  {
    value: 'treemap',
    label: 'Treemap',
    icon: <ChartIcon type="treemap" />,
    isUserSelectable: true,
  },
  {
    value: 'icicle',
    label: 'Icicle',
    icon: <ChartIcon type="icicle" />,
    isUserSelectable: true,
  },
  {
    value: 'line',
    label: 'Line',
    icon: <ChartIcon type="line" />,
    isUserSelectable: true,
  },
  {
    value: 'volume',
    label: 'Volume Dynamics',
    icon: <ChartIcon type="volume" />,
    isUserSelectable: true,
  },
  {
    value: 'sankey',
    label: 'Sankey',
    icon: <ChartIcon type="sankey" />,
    isUserSelectable: true,
  },
  {
    value: 'chord',
    label: 'Chord',
    icon: <ChartIcon type="chord" />,
    isUserSelectable: true,
  },
  {
    value: 'distribution',
    label: 'Distribution',
    icon: <ChartIcon type="distribution" />,
    isUserSelectable: true,
  },
  {
    value: 'box',
    label: 'Boxplot',
    icon: <ChartIcon type="box" />,
    isUserSelectable: true,
  },
  {
    value: 'gauge-v2',
    label: 'Gauge',
    icon: <ChartIcon type="gauge-v2" />,
    isUserSelectable: true,
  },
  {
    value: 'table',
    label: 'Table',
    icon: <ChartIcon type="table" />,
    isUserSelectable: true,
  },
  {
    value: 'text',
    label: 'Text',
    icon: <ChartIcon type="text" />,
    isUserSelectable: true,
  },
  {
    value: 'ai-summary',
    label: 'AI Summary',
    icon: <ChartIcon type="ai-summary" />,
    isUserSelectable: true,
  },

  // Non-selectable
  {
    value: 'full-profile-info',
    label: 'Full Profile Info',
    icon: <NonSelectableChartIcon />,
    isUserSelectable: false,
  },
  {
    value: 'share-of-segment',
    label: 'Share of segment',
    icon: <NonSelectableChartIcon />,
    isUserSelectable: false,
  },
  {
    value: 'entity-summary',
    label: 'Summary',
    icon: <NonSelectableChartIcon />,
    isUserSelectable: false,
  },
  {
    value: 'mixed-stats',
    label: 'Mixed Stats',
    icon: <NonSelectableChartIcon />,
    isUserSelectable: false,
  },
  {
    value: 'sentiment_review',
    label: 'Sentiment Review',
    icon: <NonSelectableChartIcon />,
    isUserSelectable: false,
  },
];

export const FIRST_SELECTABLE_CHART_TYPE = CHART_TYPE_OPTIONS.find(
  (option) => option.isUserSelectable
)?.value as SelectableChartType;

export const isUserSelectableChartType = (chartType: ChartType): boolean => {
  const option = CHART_TYPE_OPTIONS.find((opt) => opt.value === chartType);
  return option?.isUserSelectable ?? false;
};

type ChartTypeSelectorProps = {
  /** Fired on every chart-type pick so the host section can collapse. */
  onSelect?: () => void;
  item: PositionedItem;
};

export const ChartTypeSelector = ({
  onSelect,
  item,
}: ChartTypeSelectorProps) => {
  const { dispatch } = useTabContentConfig();
  const selectedChartType = item?.plot?.chartType;
  const id = item?.id;

  const handleSelect = (chartType: SingleValue<SelectableChartType>) => {
    if (!chartType) return;

    onSelect?.();

    dispatch({ type: 'UPDATE_ITEM_CHART_TYPE', payload: { id, chartType } });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  const initialUnselectableChart = useMemo(() => {
    const initialType = item?.plot?.chartType;
    if (initialType && !isUserSelectableChartType(initialType)) {
      return initialType;
    }
    return null;
  }, [item?.id]);

  const displayOptions = CHART_TYPE_OPTIONS.filter(
    (option) =>
      option.isUserSelectable ||
      option.value === selectedChartType ||
      option.value === initialUnselectableChart
  );

  return (
    <div className={layoutStyles.section}>
      <div className={styles.grid}>
        {displayOptions.map(({ value, label, icon }) => {
          const isSelected = value === selectedChartType;

          return (
            <button
              key={value}
              type="button"
              aria-pressed={isSelected}
              className={styles.cell}
              data-selected={isSelected}
              onClick={() => handleSelect(value as SelectableChartType)}
            >
              <div className={classNames(styles.icon, layoutStyles.iconFrame)}>
                {icon}
              </div>
              <Text fontSize="xx-sm" lineClamp={2} className={styles.cellLabel}>
                {label}
              </Text>
            </button>
          );
        })}
      </div>
    </div>
  );
};
